import Badge from "../badge/Badge";
import AlightPermissionIcon from "./img/alightPermission.svg";
import { useAuth } from "../../config/utilities/useAuth";
import { groupScheduledSessions } from "../../config/utilities/scheduledSession";
import {
  getTripBadgeProps,
  DIRECTION_BADGE,
} from "../../config/utilities/tripBadge";

const labelStyle = { color: "#667085", minWidth: "6rem", flexShrink: 0 };
const rowStyle = {
  display: "flex",
  gap: "0.5rem",
  alignItems: "flex-start",
  fontSize: "0.85rem",
};
const badgeWrapStyle = { display: "flex", gap: "0.4rem", flexWrap: "wrap" };

// One family child as a card: name, class, stop, and scheduled session
// (sessions grouped by direction, BBP-854). Shared by the desktop table
// (TableRow) and the mobile card view (Table) so the design stays in one place.
export default function FamilyChildCard({ child }) {
  const { tripTypes } = useAuth();
  const types = tripTypes || [];

  const sortedStops = (child.associatedStops || []).slice().sort((a, b) => {
    if (a.session === b.session) return 0;
    if (a.session === "none") return -1;
    if (b.session === "none") return 1;
    return (
      types.findIndex((t) => t.id === a.session) -
      types.findIndex((t) => t.id === b.session)
    );
  });
  const sessionLines = groupScheduledSessions(child.associatedStops, types);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        padding: "0.75rem 0.85rem",
        border: "1px solid #EAECF0",
        borderRadius: "10px",
        background: "#FFFFFF",
      }}
    >
      {/* Name and alight permission */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          fontWeight: 600,
          color: "#101828",
        }}
      >
        <span>
          {`${child.firstName ?? ""} ${child.lastName ?? ""}`.trim() || "-"}
        </span>
        {child.alightPermission && (
          <img
            src={AlightPermissionIcon}
            alt="Permission Icon"
            style={{ height: "1.25rem", verticalAlign: "middle" }}
          />
        )}
      </div>

      {/* Class */}
      {child.class && (
        <div style={rowStyle}>
          <span style={labelStyle}>Class</span>
          <span style={{ color: "#344054" }}>{child.class}</span>
        </div>
      )}

      {/* Associated stops */}
      {sortedStops.length > 0 && (
        <div style={rowStyle}>
          <span style={labelStyle}>Stop</span>
          <div style={badgeWrapStyle}>
            {sortedStops.map((stop, idx) => {
              const tripType = types.find((t) => t.id === stop.session);
              const { background, color } = getTripBadgeProps(
                stop.session,
                types,
              );
              return (
                <Badge
                  key={idx}
                  text={stop.name}
                  background={background}
                  color={color}
                  width="fit-content"
                  tooltip={
                    stop.session !== "none"
                      ? tripType?.name || stop.session
                      : undefined
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Scheduled session — grouped by direction (BBP-854) */}
      {sessionLines.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.35rem",
            paddingTop: "0.5rem",
            borderTop: "1px solid #F2F4F7",
          }}
        >
          <span
            style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475467" }}
          >
            Scheduled Session
          </span>
          <div style={badgeWrapStyle}>
            {sessionLines.map(({ label, sessions }) => {
              // Direction badge in the One-Way / Two-Way palette —
              // To School = One-Way color, From School = Two-Way color.
              // Trip types show on hover.
              const { background, color } =
                label === "To School"
                  ? DIRECTION_BADGE.oneWay
                  : DIRECTION_BADGE.twoWay;
              const tripNames = sessions
                .map((s) => types.find((t) => t.id === s)?.name || s)
                .join(", ");
              return (
                <Badge
                  key={label}
                  text={label}
                  background={background}
                  color={color}
                  width="fit-content"
                  tooltip={tripNames}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
